/**
 * WebGL wind texture upload utilities and color ramp generation.
 *
 * SpecRef: ARCH-002
 * Reference: docs/decisions/ARCH-002-webgl-heatmap-migration.md
 *
 * SpecRef: ARCH-003
 * See: docs/decisions/ARCH-003-wind-heatmap-color-themes.md#color-ramp-generation
 * Theme-aware color ramp generation with piecewise-linear stop interpolation.
 *
 * Converts equirectangular wind-speed data (number[]) into GPU-ready formats:
 * - Uint8Array wind textures  (LUMINANCE, 1 byte/texel)
 * - Uint8Array 1D color ramp  (RGBA, 256 entries = 1024 bytes)
 */

// eslint-disable-next-line import-x/no-internal-modules
import { DEFAULT_THEME } from '../model/colorThemes';
// eslint-disable-next-line import-x/no-internal-modules
import { MAX_WIND } from '../model/constants';

// eslint-disable-next-line import-x/no-internal-modules
import type { RGBA, WindColorTheme } from '../model/types';

// ── Hex parsing helper ────────────────────────────────────────────────────────

const parseHex = (hex: string): [number, number, number] => {
  const h = hex.replace('#', '');
  if (h.length <= 4) {
    return [
      Number.parseInt(h[0] + h[0], 16),
      Number.parseInt(h[1] + h[1], 16),
      Number.parseInt(h[2] + h[2], 16),
    ];
  }
  return [
    Number.parseInt(h.slice(0, 2), 16),
    Number.parseInt(h.slice(2, 4), 16),
    Number.parseInt(h.slice(4, 6), 16),
  ];
};

// ── Piecewise-linear theme interpolation ──────────────────────────────────────

/**
 * Map a wind speed to an RGB color by piecewise-linear interpolation
 * between adjacent theme gradient stops.
 *
 * - Speeds below the first stop clamp to the first stop color.
 * - Speeds above the last stop clamp to the last stop color.
 * - Between two stops: linear RGB interpolation based on speed position.
 */
export const themeColorAtSpeed = (
  speed: number,
  theme: WindColorTheme,
): [number, number, number] => {
  const { gradient } = theme;
  const first = gradient[0];
  const last = gradient.at(-1);

  if (speed <= first[0]) {
    return parseHex(first[1]);
  }
  if (last && speed >= last[0]) {
    return parseHex(last[1]);
  }

  // Find the two bracketing stops
  let lo = 0;
  for (let i = 1; i < gradient.length; i++) {
    if (gradient[i][0] >= speed) {
      lo = i - 1;
      break;
    }
  }
  const hi = lo + 1;
  const [sLo, cLo] = gradient[lo];
  const [sHi, cHi] = gradient[hi];

  const t = (speed - sLo) / (sHi - sLo);
  const [r0, g0, b0] = parseHex(cLo);
  const [r1, g1, b1] = parseHex(cHi);

  return [
    Math.round(r0 + (r1 - r0) * t),
    Math.round(g0 + (g1 - g0) * t),
    Math.round(b0 + (b1 - b0) * t),
  ];
};

// ── Wind speed ↔ Uint8 encoding ───────────────────────────────────────────────

/** Resolution: MAX_WIND / 255 ≈ 0.118 m/s per step. */
export const WIND_SPEED_RESOLUTION = MAX_WIND / 255;

/**
 * Encode a wind speed (m/s) to a Uint8 value [0..255].
 * Values above MAX_WIND are clamped; negative values are clamped to 0.
 */
export const encodeWindSpeed = (speedMs: number): number =>
  Math.min(255, Math.max(0, Math.round((speedMs * 255) / MAX_WIND)));

/**
 * Decode a Uint8 value back to wind speed (m/s).
 */
export const decodeWindSpeed = (uint8: number): number =>
  (uint8 * MAX_WIND) / 255;

// ── Color ramp ────────────────────────────────────────────────────────────────

/** Number of entries in the 1D color-ramp texture. */
export const COLOR_RAMP_SIZE = 256;

/**
 * Compute wind colour for a given speed and theme.
 * Returns RGBA where each component is in [0..255].
 *
 * Uses piecewise-linear interpolation between adjacent theme gradient stops
 * so that each speed maps to exactly the color defined by the theme.
 *
 * @param opacityMultiplier Global opacity scale factor [0..1], default 1.
 * @param threshold Normalized threshold [0..1] — speeds below `threshold * maxWind` are fully transparent.
 * @param maxWind The maximum wind speed for the current encoding range (default MAX_WIND).
 */
export const getWindColorGl = (
  windSpeed: number,
  theme: WindColorTheme = DEFAULT_THEME,
  opacityMultiplier = 1,
  threshold = 0,
  maxWind = MAX_WIND,
): RGBA => {
  const t = maxWind > 0 ? windSpeed / maxWind : 0;

  // Smooth fade-in around the threshold using a hermite (smoothstep) curve
  // over a 7% transition band so the cutoff isn't harsh.
  const FADE_WIDTH = 0.07;
  let fade = 1;
  if (threshold > 0) {
    const lo = Math.max(0, threshold - FADE_WIDTH / 2);
    const hi = threshold + FADE_WIDTH / 2;
    if (t <= lo) {
      fade = 0;
    } else if (t < hi) {
      const x = (t - lo) / (hi - lo);
      fade = x * x * (3 - 2 * x); // smoothstep
    }
  }

  const [r, g, b] = themeColorAtSpeed(windSpeed, theme);
  return [r, g, b, 255 * opacityMultiplier * fade];
};

/**
 * Build a 256-entry RGBA color ramp as a flat Uint8Array (1024 bytes).
 * Index `i` maps to wind speed `(i / 255) * maxWind`.
 * Upload as a RGBA texture of size 256×1.
 * @param opacityMultiplier Global opacity scale factor [0..1], default 1.
 * @param threshold Normalized threshold [0..1] — speeds below threshold are fully transparent.
 * @param maxWind The maximum wind speed for the current encoding range (default MAX_WIND).
 */
export const buildColorRamp = (
  theme: WindColorTheme = DEFAULT_THEME,
  opacityMultiplier = 1,
  threshold = 0,
  maxWind = MAX_WIND,
): Uint8Array => {
  const ramp = new Uint8Array(COLOR_RAMP_SIZE * 4);
  for (let i = 0; i < COLOR_RAMP_SIZE; i++) {
    const windSpeed = (i / 255) * maxWind;
    const [r, g, b, a] = getWindColorGl(
      windSpeed,
      theme,
      opacityMultiplier,
      threshold,
      maxWind,
    );
    const offset = i * 4;
    ramp[offset] = r;
    ramp[offset + 1] = g;
    ramp[offset + 2] = b;
    ramp[offset + 3] = a;
  }
  return ramp;
};

// ── Grid encoding ─────────────────────────────────────────────────────────────

/** Standard equirectangular grid dimensions (ADR spec). */
export const GRID_WIDTH = 2048;
export const GRID_HEIGHT = 1024;
export const GRID_SIZE = GRID_WIDTH * GRID_HEIGHT;

/**
 * Convert a flat array of wind-speed magnitudes (m/s) into a Uint8Array
 * suitable for uploading as a LUMINANCE texture.
 *
 * `data` is row-major (y * width + x), same layout as the BE response.
 */
export const encodeWindGrid = (
  data: number[],
  width: number,
  height: number,
): Uint8Array => {
  const len = width * height;
  const out = new Uint8Array(len);
  for (let i = 0; i < len; i++) {
    out[i] = encodeWindSpeed(data[i] ?? 0);
  }
  return out;
};
